import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Key, Copy, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RevealWmmPasswordButton — Founder-only admin action.
 *
 * Reveals the founder's WeMillionairesMM temp password ONCE. After the
 * modal closes the password is cleared server-side, so subsequent
 * reveals return `{revealed: false, reason: 'already_revealed'}`. In
 * that case the button offers a "Re-issue" fallback that generates a
 * new password and re-fires the outbound webhook + welcome email.
 *
 * Only renders for `u.is_founder` users — non-founders don't have a
 * WMM account provisioned so the button would 404 the reveal call.
 */
export default function RevealWmmPasswordButton({ user, getAuthHeader }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(null); // { revealed, temp_password?, reason?, revealed_at? }

  if (!user?.is_founder) return null;

  const openAndReveal = async () => {
    setOpen(true);
    setLoading(true);
    setState(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/founders/${user.id}/temp-password`,
        { headers: getAuthHeader() }
      );
      setState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not fetch temp password');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    if (!state?.temp_password) return;
    try {
      await navigator.clipboard.writeText(state.temp_password);
      toast.success('Password copied to clipboard');
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  const reissue = async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/founders/${user.id}/reissue-temp-password`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(`New password issued and emailed to ${user.email}`);
      // Immediately fetch + reveal the new password.
      const res = await axios.get(
        `${API_URL}/api/admin/founders/${user.id}/temp-password`,
        { headers: getAuthHeader() }
      );
      setState(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-issue failed');
    } finally {
      setLoading(false);
    }
  };

  const bodyContent = () => {
    if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

    if (state?.revealed && state?.temp_password) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            This will only be shown once. Copy it now — after this dialog closes it&apos;s gone forever from our side.
          </p>
          <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500/15 to-yellow-500/10 border border-amber-500/40 text-center">
            <p className="text-[10px] uppercase tracking-widest text-amber-500 mb-1">Temporary password</p>
            <p className="font-mono text-lg font-bold text-white break-all" data-testid="reveal-password-value">{state.temp_password}</p>
          </div>
          <Button onClick={copy} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="reveal-password-copy">
            <Copy className="w-4 h-4 mr-1" /> Copy to clipboard
          </Button>
        </div>
      );
    }

    if (state?.reason === 'already_revealed') {
      const byLine = state.revealed_by_email
        ? ` by ${state.revealed_by_email}`
        : '';
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The temp password for <strong>{user.email}</strong> was already revealed on{' '}
            {state.revealed_at ? new Date(state.revealed_at).toLocaleString() : 'a previous session'}
            <span data-testid="reveal-password-audit">{byLine}</span>.
            For security we don&apos;t keep it on file after reveal.
          </p>
          <p className="text-sm text-muted-foreground">
            Need to help this founder in? Re-issue a fresh password. We&apos;ll email the new one to them
            and re-sync it to WeMillionairesMM.
          </p>
          <Button
            onClick={reissue}
            disabled={loading}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="reveal-password-reissue"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Re-issue temp password
          </Button>
        </div>
      );
    }

    if (state?.reason === 'not_issued') {
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            No temp password has been issued for <strong>{user.email}</strong> yet — this Founder was
            provisioned before password issuance was enabled, or the initial webhook never fired.
          </p>
          <Button
            onClick={reissue}
            disabled={loading}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="reveal-password-reissue"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Issue temp password now
          </Button>
        </div>
      );
    }

    if (state?.reason === 'expired') {
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The temp password expired on {new Date(state.expired_at).toLocaleString()}. We auto-clear
            passwords after 30 days to keep them off long-term storage.
          </p>
          <Button
            onClick={reissue}
            disabled={loading}
            className="w-full bg-amber-600 hover:bg-amber-700"
            data-testid="reveal-password-reissue"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Re-issue temp password
          </Button>
        </div>
      );
    }

    return <p className="text-sm text-muted-foreground">No data returned.</p>;
  };

  return (
    <>
      <Button
        size="sm"
        onClick={openAndReveal}
        className="bg-amber-600 hover:bg-amber-700 text-xs"
        data-testid="reveal-wmm-password-btn"
        title="Reveal this Founder's WeMillionairesMM temp password (one-time)"
      >
        <Key className="w-3 h-3" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="reveal-wmm-password-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>WeMillionairesMM Temp Password</AlertDialogTitle>
            <AlertDialogDescription>
              For <strong>{user.name}</strong> ({user.email})
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">{bodyContent()}</div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reveal-password-close">Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
